'use client';

import React from 'react';
import {AdvancedFilters} from '.';
import type {AdvancedFilterData} from '.';
import {ParsedDataGFR} from '@services/new/common/getFilters';
import {ParsedDataGS} from '@services/new/common/getSuggestions';

type AdvancedFilterContextType = {
  show: (data: AdvancedFilterData) => Promise<AdvancedFilterData | null>;
};

const AdvancedFilterContext = React.createContext<
  AdvancedFilterContextType | undefined
>(undefined);

export const AdvancedFilterProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [open, setOpen] = React.useState(false);
  const [filterData, setFilterData] = React.useState<AdvancedFilterData>();
  const [resolvePromise, setResolvePromise] = React.useState<
    ((data: AdvancedFilterData | null) => void) | null
  >(null);

  const show = (data: AdvancedFilterData) => {
    setFilterData(data);
    setOpen(true);
    return new Promise<AdvancedFilterData>((resolve) => {
      setResolvePromise(() => resolve);
    });
  };

  const onClose = (submittedData?: AdvancedFilterData) => {
    setOpen(false);
    setFilterData(undefined);
    resolvePromise?.(submittedData ?? null);
  };

  return (
    <AdvancedFilterContext.Provider value={{show}}>
      {children}
      <AdvancedFilters open={open} onClose={onClose} data={filterData} />
    </AdvancedFilterContext.Provider>
  );
};

export const useAdvancedFilter = () => {
  const context = React.useContext(AdvancedFilterContext);
  if (!context) {
    throw new Error(
      'useAdvancedFilter must be used within a AdvancedFilterProvider'
    );
  }
  return context;
};
